import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Message } from 'semantic-ui-react'
import { DownloadService } from '~/api/services/downloadFiles'
import { MGAAttachmentService } from '~/api/services/mgaAttachments'
import Dialog from '~/components/Dialog'
import i18n from '~/components/i18n'
import Layout from '~/components/Layout'
import { El } from '~/components/Layout/style'
import notify from '~/components/Notify'
import TableComponent from '~/components/TableComponent'
import { FARMER_MANAGER_ROLE_SLUG, LIGHT_USER_ROLE_SLUG } from '~/Constants'
import { FarmersQuery } from '~/QueryHooks/Farmers'
import { useAuth } from '~/routes/components/AuthProvider'
import { RouteTypes } from '~/routes/constants'
import { handleErrors } from '~/utils/helpers'

const getActions = () => [
  {
    popupMessage: i18n.t('Show more'),
    type: 'icon',
    name: 'eye',
    size: 'large',
    actionType: 'preview'
  },
  {
    popupMessage: i18n.t('Delete'),
    type: 'icon',
    name: 'trash',
    size: 'large',
    actionType: 'delete'
  }
]

const MgaAttachment: React.FC = () => {
  const { t } = useTranslation()
  const actions = getActions()
  const navigate = useNavigate()
  const { id } = useParams()
  const [dataFiltered, setDataFiltered] = useState({
    rows: 10,
    page: 0,
    column: '',
    sort: ''
  })
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })
  const [deleteMga, setDeleteMga] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [tableColumnHeaders, setTableColumnHeaders] = useState([
    {
      name: t('Trading month'),
      key: 'month',
      type: 'date',
      format: 'MMM YYYY',
      sorted: 'desc'
    },
    {
      name: t('Created at'),
      key: 'createdAt',
      type: 'date',
      format: 'DD.MM.YYYY HH:mm'
    },
    {
      name: t('Actions')
    }
  ])
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const disableActions = (roleSlug === LIGHT_USER_ROLE_SLUG) || (roleSlug === FARMER_MANAGER_ROLE_SLUG)

  const {
    isLoading: isBusyFinancialData,
    data: financialData
  } = FarmersQuery.getFarmerFinancialDataQuery(id);

  const {
    isLoading: isBusyData,
    data: farmersMgaAttachments,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = FarmersQuery.getFarmerMgaAttachments(id, {
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  useEffect(() => {
    fetchFilteredData()
  }, [dataFiltered])

  function renderActions() {
    return financialData?.settings?.length !== 0 && (
      <>
        <El.PageHeaderActionButton
          size='large'
          onClick={() => navigate(RouteTypes.DAIRY_FACTORY_FARMERS_MGA_ATTACHMENT_CREATE.replace(':id', id))}
        >
          {t('Create')}
        </El.PageHeaderActionButton>
      </>
    );
  }

  async function handlePreviewMga(row) {
    setLoadingAction({
      type: 'preview',
      id: row.id,
      value: true,
      idKey: 'id'
    })
    try {
      const url = MGAAttachmentService.PREVIEW_URL.replace(':farmerId', id).replace(':mgaAttachmentId', row.id)
      await DownloadService.downloadFileInAnotherWindow(url)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function handleClickAction(row, type) {
    switch (type) {
      case 'preview':
        return handlePreviewMga(row)
      case 'delete':
        return setDeleteMga(row)
      default:
        return
    }
  }

  function renderTable() {
    return <TableComponent
      actions={disableActions ? actions.filter(action => action.actionType !== 'delete') : actions}
      isFetchingData={isFetchingData}
      animated
      loadingAction={loadingAction}
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleClickAction={handleClickAction}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      rows={rows}
      total={filtered}
    />
  }

  async function handleDeleteMga() {
    setDeleteLoading(true)
    try {
      await MGAAttachmentService.deleteMgaAttachment(id, deleteMga.id)
      notify(t('MGA attachment has been successfully deleted'), 'success')
      setDeleteMga(null)
      fetchFilteredData()
    } catch (error) {
      handleErrors(error)
    }
    setDeleteLoading(false)
  }

  function renderDeleteModal() {
    return <Dialog
      open={Boolean(deleteMga)}
      isLoading={deleteLoading}
      handleSubmit={() => handleDeleteMga()}
      handleClose={() => setDeleteMga(null)}
      textHeader={t('Delete confirmation')}
      textContent={t('Do you really want to delete MGA attachment?')}
      textSubmit={t('Delete')}
      textClose={t('Close')}
      type={'abort'}
    />
  }

  function renderNoFinancialDataMessage() {
    return !isBusyFinancialData && <Message negative style={{ textAlign: 'center' }}>
      <Message.Header>{t('Please fill in financial data for farmer')}</Message.Header>
      <p>{t('Financial data is empty for this farmer')}</p>
    </Message>
  }

  const loading = isBusyData || isBusyFinancialData
  const { filtered, rows } = farmersMgaAttachments ? farmersMgaAttachments : { filtered: 0, rows: [] };

  return <Layout
    title={t('MGA attachment')}
    isBusy={loading}
    close={RouteTypes.DAIRY_FACTORY_FARMERS}
    segmented
    actions={!disableActions && renderActions()}
  >
    {financialData?.settings?.length === 0 ? renderNoFinancialDataMessage() : renderTable()}
    {deleteMga && renderDeleteModal()}
  </Layout>
}
export default MgaAttachment